import React, { useRef, useState, useEffect } from "react";
import i18n from "i18next";

import { fetchWrapper } from "../../helpers/fetchwrapper";
import { helperMethods } from "../../helpers/helperMethods";

// Fluent UI Imports
import { baseUrl } from "../../helpers/constants";
import {
  Field,
  Input,
  InputOnChangeData,
  Label,
  Text,
  Option,
  OptionOnSelectData,
  Dropdown,
  Button,
} from "@fluentui/react-components";
import { IGetriebe } from "../../Models/IGetriebe";
import { ILuebOption } from "../../Models/ILuebOption";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import { ILubrication } from "../../Models/ILubrication";
import { IUserProduct } from "../../Models/IUserProduct";
import { IProduktDto } from "../../Models/IProdukt";
import ReactQuill from "react-quill";
import {
  formatsForTechnicalData,
  modulesForTechnicalData,
} from "../../helpers/reactQuillConstants";

interface ITechnicalDataProps {
  userProduct?: IUserProduct;
  product: IProduktDto;
  productDetails: IGetriebe;
  onReload?(): void;
}

function TechnicalData(props: ITechnicalDataProps) {
  const [descriptionMaxZeichenError, setDescriptionMaxZeichenError] =
    useState<boolean>(false);
  const [getriebe, setGetriebe] = useState<IGetriebe | undefined>(undefined);
  const handleGetriebeValuesChange = (name: string, val: any) => {
    let op = "";
    switch (name) {
      case "antriebsmoment":
        op = "SetAbtriebsmoment";
        break;
      case "abtriebsmoment":
        op = "SetAntriebsmoment";
        break;
      case "momenteneinheit":
        if (val == "Nm") {
          op = "LbsToNm";
        } else {
          op = "NmToLbs";
        }
        break;
      default:
        break;
    }

    setGetriebe((prevState: any) => ({
      ...prevState,
      [name]: val,
    }));

    setGetriebe((prevState: any) => ({
      ...prevState,
      ["operation"]: op,
    }));
  };

  const [momenteneinheit, setMomenteneinheit] = useState("Nm");
  const resetMomenteneinheit = () => {
    if (momenteneinheit == "Nm") {
      handleGetriebeValuesChange("momenteneinheit", "LbsToNm");
    }
  };

  const [lubrication, setLubrication] = useState<ILubrication>({
    anzahlVerschraubungenSchicht: 1000,
    verschraubungsart: "Normal",
    anzahlSchmierungenSchicht: 0,
    abtriebsmoment: 1.0,
    isOpenHead: false,
    id: "",
    schmierintervall: 1.0,
  });
  const handleLubricationValuesChange = (name: string, val: any) => {
    setLubrication((prevState: any) => ({
      ...prevState,
      [name]: val,
    }));
  };
  const handleUserProduktChange = (name: string, val: any) => {
    setUserProduct((prevState: any) => ({
      ...prevState,
      [name]: val,
    }));
  };

  let momenteneinheitOptions: ILuebOption[] = [
    { key: "Nm", text: "Nm" },
    { key: "in.lbs", text: "in.lbs" },
  ];

  let verschraubungsartOptions: ILuebOption[] = [
    { key: "Normal", text: i18n.t<string>("Normalgewinde") },
    { key: "Micro", text: i18n.t<string>("Microverkapselung") },
    { key: "Verformt", text: i18n.t<string>("Verformung") },
    { key: "Blechschraube", text: i18n.t<string>("Blechschraube") },
    { key: "Kunststoffschraube", text: i18n.t<string>("Kunststoffschraube") },
    { key: "Holzschraube", text: i18n.t<string>("Holzschraube") },
  ];

  useEffect(() => {
    setUserProduct(props.userProduct);
  }, [props.userProduct]);

  useEffect(() => {
    const _getriebe = props.productDetails;
    _getriebe.id = props.userProduct?.userProduktGuid;
    _getriebe.momenteneinheit = "Nm";
    setGetriebe(_getriebe);
  }, [props.productDetails]);

  useEffect(() => {
    if (props.userProduct?.userProduktGuid) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserProduct/GetDetails?guid=${props.userProduct?.userProduktGuid}`
        )
        .then((data) => {
          if (props.userProduct?.userProduktGuid) {
            data.getriebe.id = props.userProduct?.userProduktGuid;
            data.getriebe.momenteneinheit = getriebe?.momenteneinheit;
            data.getriebe.antriebsmoment = data.getriebe.antriebsmoment
              ? data.getriebe.antriebsmoment.toLocaleString(undefined, {
                  maximumFractionDigits: 3,
                })
              : 0;
            data.getriebe.abtriebsmoment = data.getriebe.abtriebsmoment
              ? data.getriebe.abtriebsmoment.toLocaleString(undefined, {
                  maximumFractionDigits: 3,
                })
              : 0;
            setGetriebe(data.getriebe);

            const abtriebsmoment = parseFloat(data.getriebe.abtriebsmoment);
            const schmierintervall = parseFloat(
              data.schmierintervall ? data.schmierintervall : 0
            );

            // Schmierung
            setLubrication({
              anzahlVerschraubungenSchicht: data.anzahlVerschraubungenSchicht
                ? Number(data.anzahlVerschraubungenSchicht)
                : 1000,
              verschraubungsart: data.verschraubungsart
                ? data.verschraubungsart
                : "Normal",
              abtriebsmoment: abtriebsmoment,
              anzahlSchmierungenSchicht: data.anzahlSchmierungenSchicht
                ? data.anzahlSchmierungenSchicht
                : 1.0,
              schmierintervall: schmierintervall,
              isOpenHead: data.isOpenHead,
              id: props.userProduct?.userProduktGuid,
            });
          }
        })
        .catch((error) => {});
    }
  }, [props.userProduct?.userProduktGuid]);

  useEffect(() => {
    if (
      getriebe &&
      (getriebe.operation == "LbsToNm" || getriebe.operation == "NmToLbs")
    )
      getGetriebe();
  }, [getriebe?.operation]);

  const getGetriebe = () => {
    if (getriebe && getriebe.id && getriebe.operation != "") {
      if (!getriebe.antriebsmoment) {
        getriebe.antriebsmoment = 0.0;
      } else {
        getriebe.antriebsmoment = helperMethods.convertToNumber(
          getriebe.antriebsmoment
        );
      }

      if (!getriebe.abtriebsmoment) {
        getriebe.abtriebsmoment = 0.0;
      } else {
        getriebe.abtriebsmoment = helperMethods.convertToNumber(
          getriebe.abtriebsmoment
        );
      }
      fetchWrapper
        .postWithAuth(`${baseUrl}/api/getriebe/PostGetriebe`, getriebe)
        .then((data: IGetriebe) => {
          data.operation = "";
          data.antriebsmoment = data.antriebsmoment ? data.antriebsmoment : 0;
          data.abtriebsmoment = data.abtriebsmoment ? data.abtriebsmoment : 0;

          data.momenteneinheit = getriebe.momenteneinheit;
          setGetriebe(data);
        })
        .catch((error) => {});
    }
  };

  const getLubrication = (lubrication: ILubrication) => {
    if (lubrication.id && lubrication.isOpenHead != null) {
      fetchWrapper
        .postWithAuth(`${baseUrl}/api/Lubrication/PostLubrication`, lubrication)
        .then((data) => {
          setLubrication(data);
        })
        .catch((error) => {});
    }
  };
  const [userProduct, setUserProduct] = useState<IUserProduct>();

  const editUserProduct = () => {
    if (userProduct) {
      var newUserProduct = { ...props.userProduct };
      newUserProduct.kundenProdukttext = userProduct.kundenProdukttext;
      newUserProduct.kundenProduktNummer = userProduct.kundenProduktNummer;
      fetchWrapper
        .postWithAuth(`${baseUrl}/api/userproduct/edit`, newUserProduct)
        .then((data) => {
          if (props.onReload) {
            props.onReload();
          }
        })
        .catch((error) => {});
    }
  };

  return (
    <>
      {props.userProduct && userProduct && (
        <div
          className="lueb-InnerContentBlockPadding2px"
          style={{ padding: 10 }}
        >
          <Field
            label={i18n.t<string>("userProductNumber")}
            style={{ maxWidth: "250px" }}
          >
            <Input
              value={userProduct.kundenProduktNummer}
              onChange={(ev, data) => {
                handleUserProduktChange(
                  "kundenProduktNummer",
                  data.value ?? ""
                );
              }}
            />
          </Field>
          <div style={{ overflow: "hidden", overflowX: "auto" }}>
            <Field
              style={{
                marginBottom: "50px",
                marginTop: "20px",
              }}
              label={i18n.t<string>("myDescription")}
              validationMessage={
                descriptionMaxZeichenError ? "Max. 200 Zeichen" : ""
              }
            >
              <ReactQuill
                modules={modulesForTechnicalData}
                formats={formatsForTechnicalData}
                theme="snow"
                className={"ql-comment-editor"}
                value={userProduct?.kundenProdukttext}
                onChange={(value) => {
                  if (value.length <= 200) {
                    setDescriptionMaxZeichenError(false);
                    handleUserProduktChange("kundenProdukttext", value ?? "");
                  } else {
                    setDescriptionMaxZeichenError(true);
                  }
                }}
              />
            </Field>
          </div>

          <Button
            appearance="primary"
            disabled={
              (userProduct.kundenProdukttext ==
                props.userProduct.kundenProdukttext ||
                descriptionMaxZeichenError == true) &&
              userProduct.kundenProduktNummer ==
                props.userProduct.kundenProduktNummer
            }
            onClick={() => {
              editUserProduct();
            }}
          >
            {i18n.t<string>("save")}
          </Button>
        </div>
      )}
      <div style={{ marginTop: 15 }}>
        <h5 style={{ marginLeft: 5 }}>{i18n.t("generalInformation")}</h5>
        <div
          className="lueb-InnerContentBlockPadding2px"
          style={{ padding: 10 }}
        >
          <div className="lueb-WrapContainer200 lueb-MarginTop20">
            <Field label={i18n.t<string>("systemId")}>
              <Input readOnly value={props.product?.produktId} />
            </Field>

            <Field label={i18n.t<string>("benennung")}>
              <Input
                readOnly
                value={
                  i18n.language.includes("de")
                    ? props.product?.artikel?.benennungDE
                    : props.product?.artikel?.benennungEN
                }
              />
            </Field>

            <Field label={i18n.t<string>("auftrag")}>
              <Input readOnly value={props.product?.auftragId} />
            </Field>

            <Field label={i18n.t<string>("auftragPos")}>
              <Input readOnly value={props.product?.auftragpos?.toString()} />
            </Field>
            {props.product?.ausgangsdatum && (
              <Field label={i18n.t<string>("lieferdatum")}>
                <Input
                  readOnly
                  value={new Date(
                    props.product?.ausgangsdatum
                  ).toLocaleDateString()}
                />
              </Field>
            )}
          </div>
        </div>
      </div>

      {props.product?.artikel &&
        !props.product?.artikel?.artikelId?.includes("VSBT") &&
        !props.product?.artikel?.benennungDE?.includes("L.ADU") && (
          <div style={{ marginTop: 15 }}>
            <h5 style={{ marginLeft: 5 }}>{i18n.t("technicalData")}</h5>
            <div
              className="lueb-InnerContentBlockPadding2px"
              style={{ padding: 10 }}
            >
              <Text
                style={{
                  fontSize: "1rem",
                  color: "#ff6600",
                  fontWeight: "normal",
                  marginTop: 10,
                }}
              >
                {i18n.t<string>("technicalDataSub")}
              </Text>

              <div className="lueb-WrapContainer200 lueb-MarginBottom20">
                {getriebe && (
                  <Field label={i18n.t<string>("maxAntriebsmoment")}>
                    <Input
                      readOnly
                      inputMode="decimal"
                      value={helperMethods.nachkomma(
                        getriebe.maxAntriebsmoment,
                        3
                      )}
                    />
                  </Field>
                )}

                {getriebe && (
                  <Field label={i18n.t<string>("maxAbtriebsmoment")}>
                    <Input
                      readOnly
                      inputMode="decimal"
                      value={helperMethods.nachkomma(
                        getriebe.maxAbtriebsmoment,
                        3
                      )}
                    />
                  </Field>
                )}

                {getriebe && (
                  <Field
                    label={
                      <Label>
                        <span>{i18n.t<string>("wirkungsgrad")}</span>
                        <span
                          style={{
                            marginLeft: "5px",
                            color: "#ff6600",
                            fontWeight: "bold",
                          }}
                        >
                          *
                        </span>
                      </Label>
                    }
                  >
                    <Input
                      readOnly
                      inputMode="decimal"
                      value={helperMethods.nachkomma(getriebe.wirkungsgrad, 3)}
                    />
                  </Field>
                )}

                {getriebe && (
                  <Field label={i18n.t<string>("uebersetzung")}>
                    <Input
                      readOnly
                      inputMode="decimal"
                      value={helperMethods.nachkomma(getriebe.uebersetzung, 4)}
                    />
                  </Field>
                )}
              </div>

              <Text
                style={{
                  fontSize: "1rem",
                  color: "#ff6600",
                  fontWeight: "normal",
                }}
              >
                {i18n.t<string>("technicalDataTorqueCalc")}
              </Text>

              {getriebe && (
                <div className="lueb-WrapContainer200">
                  <Field label={i18n.t<string>("momenteneinheit")}>
                    <Dropdown
                      value={
                        momenteneinheitOptions.find(
                          (x) => x.key == getriebe.momenteneinheit
                        )?.text
                      }
                      onOptionSelect={(ev: any, data: OptionOnSelectData) => {
                        if (data.optionValue) {
                          setMomenteneinheit(data.optionValue);
                          handleGetriebeValuesChange(
                            "momenteneinheit",
                            data.optionValue
                          );
                        }
                      }}
                      selectedOptions={
                        getriebe?.momenteneinheit
                          ? [getriebe?.momenteneinheit]
                          : []
                      }
                    >
                      {momenteneinheitOptions.map((option) => (
                        <Option key={option.key}>{option.text}</Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <Field label={i18n.t<string>("antriebsmoment")}>
                    <Input
                      inputMode="decimal"
                      onBlur={() => getGetriebe()}
                      value={getriebe?.antriebsmoment?.toString()}
                      onKeyDown={(e: any) => {
                        if (e.key == "Enter") {
                          getGetriebe();
                        }
                      }}
                      onChange={(ev: any, data: InputOnChangeData) => {
                        if (getriebe) {
                          handleGetriebeValuesChange(
                            "antriebsmoment",
                            Number(data.value)
                          );
                        }
                      }}
                    />
                  </Field>

                  <Field label={i18n.t<string>("abtriebsmoment")}>
                    <Input
                      inputMode="decimal"
                      onBlur={() => getGetriebe()}
                      value={getriebe?.abtriebsmoment?.toString()}
                      onKeyDown={(e: any) => {
                        if (e.key == "Enter") {
                          getGetriebe();
                        }
                      }}
                      onChange={(ev: any, data: InputOnChangeData) => {
                        if (getriebe) {
                          handleGetriebeValuesChange(
                            "abtriebsmoment",
                            Number(data.value)
                          );
                        }
                      }}
                    />
                  </Field>
                </div>
              )}

              <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                <Text
                  style={{
                    color: "#ff6600",
                    marginRight: "5px",
                    fontWeight: "bold",
                  }}
                >
                  *
                </Text>
                <Text>{i18n.t<string>("wirkungsgradText")}</Text>
              </div>

              {lubrication.id && lubrication.isOpenHead != null && (
                <div style={{ marginBottom: "20px" }}>
                  <StackShim horizontal verticalAlign="start">
                    <h5>{i18n.t<string>("recommendationForLubrication")}</h5>
                    <span
                      style={{
                        marginLeft: "5px",
                        color: "#ff6600",
                        fontWeight: "bold",
                      }}
                    >
                      *
                    </span>
                  </StackShim>

                  <div className="lueb-WrapContainer300">
                    <Field
                      label={i18n.t<string>("anzahlVerschraubungenSchicht")}
                    >
                      <Input
                        inputMode="decimal"
                        value={lubrication.anzahlVerschraubungenSchicht.toLocaleString()}
                        onBlur={() => {
                          getLubrication(lubrication);
                        }}
                        onKeyDown={(e: any) => {
                          if (e.key == "Enter") {
                            getLubrication(lubrication);
                          }
                        }}
                        onChange={(ev, data) =>
                          handleLubricationValuesChange(
                            "anzahlVerschraubungenSchicht",
                            data.value
                          )
                        }
                      />
                    </Field>

                    <Field label={i18n.t<string>("verschraubungsart")}>
                      <Dropdown
                        value={
                          verschraubungsartOptions.find(
                            (x) => x.key == lubrication.verschraubungsart
                          )?.text
                        }
                        onOptionSelect={(ev: any, data: OptionOnSelectData) => {
                          if (data.optionValue) {
                            handleLubricationValuesChange(
                              "verschraubungsart",
                              data.optionValue
                            );

                            const _lubrication = lubrication;
                            _lubrication.verschraubungsart = data.optionValue;

                            getLubrication(_lubrication);
                          }
                        }}
                        selectedOptions={
                          lubrication?.verschraubungsart
                            ? [lubrication?.verschraubungsart]
                            : []
                        }
                      >
                        {verschraubungsartOptions.map((vart) => {
                          return <Option key={vart.key}>{vart.text}</Option>;
                        })}
                      </Dropdown>
                    </Field>
                  </div>
                  <div className="lueb-MarginTop20 lueb-WrapContainer300">
                    <Field label={i18n.t<string>("anzahlSchmierungenSchicht")}>
                      <Input
                        readOnly
                        inputMode="decimal"
                        value={lubrication.anzahlSchmierungenSchicht.toLocaleString()}
                      />
                    </Field>

                    <Field label={i18n.t<string>("schmierintervall")}>
                      <Input
                        readOnly
                        inputMode="decimal"
                        value={lubrication.schmierintervall.toLocaleString()}
                      />
                    </Field>
                  </div>

                  <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                    <Text
                      style={{
                        color: "#ff6600",
                        marginRight: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      *
                    </Text>
                    <Text>{i18n.t<string>("lubricationText")}</Text>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
    </>
  );
}

export default TechnicalData;
